import {Box, Button, IconButton, Stack} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {LiaBroomSolid} from "react-icons/lia";
import useApiRequest from "../../hooks/useApiRequest";
import SearchAutocomplete from "../ui/SearchAutocomplete";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../../hooks/useAuthPermissions";

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const TenantUserRolesFilterBar = ({tenantId, productId, userId, roleId, onSearch}) => {
  const [filterTenantId, setFilterTenantId] = useState(tenantId || "");
  const [filterProductId, setFilterProductId] = useState(productId || "");
  const [filterUserId, setFilterUserId] = useState(userId || "");
  const [filterRoleId, setFilterRoleId] = useState(roleId || "");

  const [filterTenantInput, setFilterTenantInput] = useState("");
  const [filterProductInput, setFilterProductInput] = useState("");
  const [filterUserInput, setFilterUserInput] = useState("");
  const [filterRoleInput, setFilterRoleInput] = useState("");

  const [filterTenantValue, setFilterTenantValue] = useState(null);
  const [filterProductValue, setFilterProductValue] = useState(null);
  const [filterUserValue, setFilterUserValue] = useState(null);
  const [filterRoleValue, setFilterRoleValue] = useState(null);

  const {can} = useAuthPermissions();
  const canReadTenants = can(ACTIONS.READ, RESOURCES.TENANTS);
  const canReadProducts = can(ACTIONS.READ, RESOURCES.PRODUCTS);
  const canReadUsers = can(ACTIONS.READ, RESOURCES.USERS);
  const canReadRoles = can(ACTIONS.READ, RESOURCES.ROLES);

  const {
    data: dataFilterTenants,
    loading: loadingFilterTenants,
    list: listFilterTenants
  } = useApiRequest({baseUrl: "/tenants", resource: "tenants"});
  const {
    data: dataFilterProducts,
    loading: loadingFilterProducts,
    list: listFilterProducts
  } = useApiRequest({baseUrl: "/products", resource: "products"});
  const {
    data: dataFilterUsers,
    loading: loadingFilterUsers,
    list: listFilterUsers
  } = useApiRequest({baseUrl: "/users", resource: "users"});
  const {
    data: dataFilterRoles,
    loading: loadingFilterRoles,
    list: listFilterRoles
  } = useApiRequest({baseUrl: "/roles", resource: "roles"});

  // Initial load of autocomplete options
  useEffect(() => {
    if (!tenantId && canReadTenants) listFilterTenants(undefined, {silentError: true});
    if (!productId && canReadProducts) listFilterProducts(undefined, {silentError: true});
    if (!userId && canReadUsers) listFilterUsers(undefined, {silentError: true});
    if (!roleId && canReadRoles)
      listFilterRoles(productId ? {product_id: productId} : undefined, {silentError: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadTenants, canReadProducts, canReadUsers, canReadRoles]);

  // Sync fixed props and trigger initial grid search
  useEffect(() => {
    setFilterTenantId(tenantId || "");
    setFilterProductId(productId || "");
    setFilterUserId(userId || "");
    setFilterRoleId(roleId || "");
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (productId) params.product_id = productId;
    if (userId) params.user_id = userId;
    if (roleId) params.role_id = roleId;
    onSearch(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, productId, userId, roleId]);

  const filterTenantOptions = useMemo(
    () => normalizeArray(dataFilterTenants).map((t) => ({id: t.id, label: t.name || t.id})),
    [dataFilterTenants]
  );
  const filterProductOptions = useMemo(
    () => normalizeArray(dataFilterProducts).map((p) => ({id: p.id, label: p.name || p.id})),
    [dataFilterProducts]
  );
  const filterUserOptions = useMemo(
    () => normalizeArray(dataFilterUsers).map((u) => ({id: u.id, label: u.email || u.id})),
    [dataFilterUsers]
  );
  const filterRoleOptions = useMemo(
    () =>
      normalizeArray(dataFilterRoles).map((r) => ({
        id: r.id,
        label: [r.product_name, r.name || r.id].filter(Boolean).join(".")
      })),
    [dataFilterRoles]
  );

  const buildParams = ({tId = filterTenantId, pId = filterProductId, uId = filterUserId, rId = filterRoleId} = {}) => {
    const params = {};
    if (tId) params.tenant_id = tId;
    if (pId) params.product_id = pId;
    if (uId) params.user_id = uId;
    if (rId) params.role_id = rId;
    return params;
  };

  const handleSearch = () => onSearch(buildParams());

  const handleClear = () => {
    setFilterTenantId(tenantId || "");
    setFilterProductId(productId || "");
    setFilterUserId(userId || "");
    setFilterRoleId(roleId || "");
    setFilterTenantInput("");
    setFilterProductInput("");
    setFilterUserInput("");
    setFilterRoleInput("");
    setFilterTenantValue(null);
    setFilterProductValue(null);
    setFilterUserValue(null);
    setFilterRoleValue(null);

    if (!roleId && canReadRoles) {
      listFilterRoles(productId ? {product_id: productId} : undefined, {silentError: true});
    }

    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (productId) params.product_id = productId;
    if (userId) params.user_id = userId;
    if (roleId) params.role_id = roleId;
    onSearch(params);
  };

  return (
    <Box sx={{mb: 2}}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {!tenantId && canReadTenants && (
          <SearchAutocomplete
            label="Tenant"
            options={filterTenantOptions}
            loading={loadingFilterTenants}
            value={filterTenantValue}
            inputValue={filterTenantInput}
            onInputChange={(_, v) => setFilterTenantInput(v)}
            onChange={(_, value) => {
              setFilterTenantValue(value);
              setFilterTenantId(value?.id || "");
            }}
            onSearch={() =>
              listFilterTenants(filterTenantInput ? {name: filterTenantInput} : undefined, {silentError: true})
            }
          />
        )}
        {!userId && canReadUsers && (
          <SearchAutocomplete
            label="Usuario"
            options={filterUserOptions}
            loading={loadingFilterUsers}
            value={filterUserValue}
            inputValue={filterUserInput}
            onInputChange={(_, v) => setFilterUserInput(v)}
            onChange={(_, value) => {
              setFilterUserValue(value);
              setFilterUserId(value?.id || "");
            }}
            onSearch={() =>
              listFilterUsers(filterUserInput ? {email: filterUserInput} : undefined, {silentError: true})
            }
          />
        )}
        {!productId && canReadProducts && (
          <SearchAutocomplete
            label="Producto"
            options={filterProductOptions}
            loading={loadingFilterProducts}
            value={filterProductValue}
            inputValue={filterProductInput}
            onInputChange={(_, v) => setFilterProductInput(v)}
            onChange={(_, value) => {
              setFilterProductValue(value);
              setFilterProductId(value?.id || "");
              // Reload roles filtered by selected product and clear current role selection
              setFilterRoleValue(null);
              setFilterRoleId("");
              setFilterRoleInput("");
              if (canReadRoles) listFilterRoles(value?.id ? {product_id: value.id} : undefined, {silentError: true});
            }}
            onSearch={() =>
              listFilterProducts(filterProductInput ? {name: filterProductInput} : undefined, {silentError: true})
            }
          />
        )}
        {!roleId && canReadRoles && (
          <SearchAutocomplete
            label="Rol"
            options={filterRoleOptions}
            loading={loadingFilterRoles}
            value={filterRoleValue}
            inputValue={filterRoleInput}
            onInputChange={(_, v) => setFilterRoleInput(v)}
            onChange={(_, value) => {
              setFilterRoleValue(value);
              setFilterRoleId(value?.id || "");
            }}
            onSearch={() => {
              const roleParams = {};
              const activeProductId = productId || filterProductId;
              if (activeProductId) roleParams.product_id = activeProductId;
              if (filterRoleInput) roleParams.name = filterRoleInput;
              listFilterRoles(Object.keys(roleParams).length ? roleParams : undefined, {silentError: true});
            }}
          />
        )}
        <Button variant="contained" size="small" onClick={handleSearch}>
          Aplicar
        </Button>
        <IconButton onClick={handleClear}>
          <LiaBroomSolid />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default TenantUserRolesFilterBar;
